import { useState } from "react"
import { Helmet } from "react-helmet"
import toast from "react-hot-toast";

import DataTable from "react-data-table-component";
import Modal from "./Modal";
import TicketView from "./TicketView";
import UpdateTicketForm from "./UpdateTicketForm";
import AddTicketForm from "./AddTicketForm";
import ConfirmPrompt from "./ConfirmPrompt";
import TableLoader from "./TableLoader";
import SectionHeader from "./SectionHeader";
import FilterSelect from "./FilterSelect";
import DateRangePicker from "@wojtekmaj/react-daterange-picker";

import supportTicketService from "../services/supportTicketService";
import useSupportTickets from "../hooks/useSupportTickets";

import { formatDateToSydneyTimeZone } from "../utils/dateUtils";

import supportTicketStatuses from "../data/supportTicketStatuses.json";
import supportTicketPriorities from "../data/supportTicketPriorities.json";

const AdminSupport = () => {
  const [selectedTicket, setSelectedTicket] = useState(null);

  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState(null);
  const [dateRange, setDateRange] = useState([null, null]);

  const [selectedStatus, setSelectedStatus] = useState(null);
  const [selectedPriority, setSelectedPriority] = useState(null);
  const [isDeletingTicket, setIsDeletingTicket] = useState(false);

  const { data, isLoading, mutate } = useSupportTickets(
    {
      status: selectedStatus?.value || null,
      priority: selectedPriority?.value || null,
      startDate: dateRange?.[0] || null,
      endDate: dateRange?.[1] || null,
      page,
      perPage,
      sortField,
      sortOrder
    },
    { refreshInterval: 5 * 1000 }
  );

  const supportTickets = data?.supportTickets;
  const supportTicketCount = data?.count;

  const [isCreateTicketModalOpen, setIsCreateTicketModalOpen] = useState(false);
  const [isUpdateTicketModalOpen, setIsUpdateTicketModalOpen] = useState(false);
  const [isViewTicketModalOpen, setIsTicketViewModalOpen] = useState(false);
  const [isConfirmDeleteModalOpen, setIsConfirmDeleteModalOpen] = useState(false);

  const handlePageChange = page => {
    setPage(page);
  };

  const handlePerRowsChange = async (newPerPage, page) => {
    setPerPage(newPerPage);
    setPage(page);
  };

  const handleUpdateClicked = () => {
    setIsUpdateTicketModalOpen(true);
  }

  const handleRowClicked = (row) => {
    setSelectedTicket(row);
    setIsTicketViewModalOpen(true);
  }

  const handleCloseViewTicket = () => {
    mutate();
    setIsTicketViewModalOpen(false);
  }

  const handleDeleteClicked = (row) => {
    setSelectedTicket(row);
    setIsConfirmDeleteModalOpen(true);
  }

  const handleDeleteConfirmed = async () => {
    try {
      setIsDeletingTicket(true);
      await supportTicketService.deleteSupportTicket(selectedTicket._id);

      mutate();
      toast.success('Ticket deleted');
      setIsTicketViewModalOpen(false);
    } catch (error) {
      toast.error('An error occurred while deleting the ticket');
    } finally {
      setIsDeletingTicket(false);
      setIsConfirmDeleteModalOpen(false);
    }
  }

  const handleCreateTicket = async (ticket) => {
    try {
      setIsCreateTicketModalOpen(false);

      await supportTicketService.createSupportTicket(ticket);

      mutate();

      toast.success('Ticket created');
    } catch (error) {
      toast.error('An error occurred while creating the ticket');
    }
  }

  const handleUpdateTicket = async (ticketData) => {
    try {
      setIsUpdateTicketModalOpen(false);
      setIsTicketViewModalOpen(false);

      await supportTicketService.updateSupportTicket({
        ticketId: selectedTicket._id,
        ...ticketData
      });

      mutate();

      toast.success('Ticket updated');
    } catch (error) {
      toast.error('An error occurred while updating the ticket');
    }
  }

  const handleSort = async (column, sortDirection) => {
    setSortField(column.sortField);
    setSortOrder(sortDirection === 'desc' ? 1 : -1);
  };

  const columns = [
    {
      name: "Title",
      selector: (row) => row.title,
      grow: 2,
    },
    {
      name: "Status",
      selector: (row) => row.status
    },
    {
      name: "Priority",
      selector: (row) => row.priority
    },
    {
      name: "Date",
      selector: (row) => row.createdAt,
      format: (row) => formatDateToSydneyTimeZone(row.createdAt)
    },
    {
      name: "Created by",
      selector: (row) => row.createdBy.fullName
    },
    {
      name: '',
      right: "true",
      cell: row => (
        <div className='flex items-center gap-2'>
          <button
            onClick={() => handleDeleteClicked(row)}
            className='py-1 px-2 text-xs font-semibold cursor-pointer rounded-md bg-red-600 text-white'
          >
            Delete
          </button>
        </div>
      )
    }
  ];

  return (
    <>
      <Helmet>
        <title>Support | ASE Group</title>
      </Helmet>

      <div className='border border-ase-blue rounded-lg pb-1'>
        <SectionHeader
          title='Tickets'
          actionsLeft={
            <button
              onClick={() => setIsCreateTicketModalOpen(true)}
              className='px-2 py-1 bg-white rounded-md text-ase-blue font-semibold text-xs'
            >
              Add
            </button>
          }
          actionsRight={(
            <div className='flex flex-col md:flex-row md:items-center gap-2 w-full'>
              <p className='text-white text-xs'>Status:</p>
              <div className='text-xs  h-[24px]'>
                <FilterSelect
                  options={supportTicketStatuses}
                  value={selectedStatus}
                  onChange={setSelectedStatus}
                  placeholder='Select status'
                />
              </div>

              <p className='text-white text-xs'>Priority:</p>
              <div className='text-xs  h-[24px]'>
                <FilterSelect
                  options={supportTicketPriorities}
                  value={selectedPriority}
                  onChange={setSelectedPriority}
                  placeholder='Select priority'
                />
              </div>

              <p className='text-white text-xs'>Period:</p>

              <DateRangePicker
                value={dateRange}
                onChange={setDateRange}
                format='dd/MM/y'
                className="filter-date-range"
              />
            </div>
          )}
        />

        <DataTable
          columns={columns}
          data={supportTickets}
          striped
          highlightOnHover
          pointerOnHover
          onRowClicked={handleRowClicked}
          progressPending={isLoading}
          progressComponent={<TableLoader />}
          sortServer
          onSort={handleSort}
          pagination
          paginationServer
          paginationTotalRows={supportTicketCount}
          paginationRowsPerPageOptions={[10, 20, 50]}
          paginationPerPage={perPage}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handlePerRowsChange}
        />
      </div>

      <Modal isOpen={isCreateTicketModalOpen}>
        <AddTicketForm
          onSubmit={handleCreateTicket}
          onCloseClicked={e => setIsCreateTicketModalOpen(false)}
        />
      </Modal>

      <Modal isOpen={isUpdateTicketModalOpen}>
        <UpdateTicketForm
          ticket={selectedTicket}
          onSubmit={handleUpdateTicket}
          onCloseClicked={e => setIsUpdateTicketModalOpen(false)}
        />
      </Modal>

      <Modal isOpen={isViewTicketModalOpen}>
        <TicketView
          ticketId={selectedTicket?._id}
          onUpdateClicked={handleUpdateClicked}
          onDeleteClicked={setIsConfirmDeleteModalOpen}
          onCloseClicked={handleCloseViewTicket}
        />
      </Modal>

      <Modal isOpen={isConfirmDeleteModalOpen}>
        <ConfirmPrompt
          message={`Are you sure you want to delete the ${selectedTicket?.title} ticket?`}
          onConfirm={() => handleDeleteConfirmed()}
          onCancel={() => setIsConfirmDeleteModalOpen(null)}
          isLoading={isDeletingTicket}
        />
      </Modal>
    </>
  )
}

export default AdminSupport;
